import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Badge } from './ui'
import type { OrganizationInvitation } from '../api/organizations'

interface InvitationTableProps {
  invitations: OrganizationInvitation[]
  isOwnerOrAdmin: boolean
  onCancel: (invitationId: string) => void
  onResend: (invitation: OrganizationInvitation) => void
}

export const InvitationTable: React.FC<InvitationTableProps> = ({
  invitations,
  isOwnerOrAdmin,
  onCancel,
  onResend,
}) => {
  const intl = useIntl()
  const [resendingId, setResendingId] = useState<string | null>(null)

  const handleResend = async (invitation: OrganizationInvitation) => {
    setResendingId(invitation.id)
    try {
      await onResend(invitation)
    } finally {
      setResendingId(null)
    }
  }

  if (invitations.length === 0) {
    return (
      <div className="text-center py-8 text-foreground-muted">
        <FormattedMessage id="invitations.empty" />
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="invitations.col.email" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="invitations.col.role" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="invitations.col.status" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="invitations.col.sent" /></th>
            {isOwnerOrAdmin && (
              <th className="pb-3 font-semibold text-foreground text-right"><FormattedMessage id="invitations.col.actions" /></th>
            )}
          </tr>
        </thead>
        <tbody>
          {invitations.map((invitation) => (
            <tr key={invitation.id} className="border-b border-border last:border-0">
              <td className="py-3 font-medium text-foreground">{invitation.email}</td>
              <td className="py-3">
                <Badge variant="neutral">
                  {intl.formatMessage({ id: `invitations.role.${invitation.role}`, defaultMessage: invitation.role })}
                </Badge>
              </td>
              <td className="py-3">
                <Badge variant="warning">
                  {intl.formatMessage({ id: `invitations.status.${invitation.status}`, defaultMessage: invitation.status })}
                </Badge>
              </td>
              <td className="py-3 text-foreground-muted">
                {new Date(invitation.created_at).toLocaleDateString()}
              </td>
              {isOwnerOrAdmin && (
                <td className="py-3 text-right">
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      loading={resendingId === invitation.id}
                      onClick={() => handleResend(invitation)}
                    >
                      <FormattedMessage id="invitations.resend" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCancel(invitation.id)}
                      className="text-error hover:text-error"
                    >
                      <FormattedMessage id="invitations.cancel" />
                    </Button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
